/**
 * Monday.com Contacts & Accounts Domain Service
 *
 * Standalone functions for contact and account CRUD operations,
 * extracted from the monolithic MondayService class.
 * Each function takes a MondayCore instance as its first parameter.
 */

import {
  MondayCore,
  hubItemToMondayItem,
} from './mondayCore';
import type {
  MondayContact,
  HubBoardItem,
  HubBoardItemsPage,
} from './mondayCore';
import { mondayClient } from './client';
import { logger } from '@/utils/logger';

// ============================================================================
// Contact Types
// ============================================================================

export interface ContactData {
  firstName?: string;
  lastName?: string;
  name?: string;
  email: string;
  phone?: string;
  title?: string;
  company?: string;
  seniority?: string;
  departments?: string;
  owner?: string;
  website?: string;
  linkedinUrl?: string;
  city?: string;
  state?: string;
  country?: string;
  industry?: string;
  employeeCount?: string;
  technologies?: string;
  accountId?: string;
}

export interface ContactUpdates {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  title?: string;
  company?: string;
  seniority?: string;
  departments?: string;
  owner?: string;
  website?: string;
  linkedinUrl?: string;
  city?: string;
  state?: string;
  country?: string;
  industry?: string;
  employeeCount?: string;
  technologies?: string;
}

export interface AccountData {
  website?: string;
  industry?: string;
  size?: string;
  location?: string;
}

export interface CompanyData {
  company: string;
  website?: string;
  industry?: string;
  size?: string;
  location?: string;
  linkedin?: string;
  technologies?: string[];
}

export interface SyncResult {
  created: number;
  updated: number;
  failed: number;
  errors: string[];
}

// ============================================================================
// Contact Functions
// ============================================================================

/**
 * Get contacts from Monday board
 */
export async function getContacts(_core: MondayCore): Promise<MondayContact[]> {
  const contacts = await mondayClient.get<MondayContact[]>('/hub/contacts', {
    cache: true,
    cacheTTL: 60 * 60 * 1000,
  });
  logger.log(`[Hub] Total contacts fetched: ${contacts.length}`);
  return contacts;
}

/**
 * Find a contact by email in the Contacts board
 */
export async function findContactByEmail(core: MondayCore, email: string): Promise<MondayContact | null> {
  const config = core.getConfig();

  if (!config.contactsBoardId || !email) {
    return null;
  }

  try {
    const page = await mondayClient.get<HubBoardItemsPage>(
      `/hub/items/contacts/contact_email?column_value=${encodeURIComponent(email)}&limit=1`,
    );
    if (!page?.data) return null;
    const hubItem = page.data[0];
    if (!hubItem) return null;

    const item = hubItemToMondayItem(hubItem);
    const getValue = (columnId: string): string => {
      const col = item.column_values.find((c) => c.id === columnId);
      return col?.text ?? '';
    };

    return {
      id: item.id,
      name: item.name,
      email: getValue('contact_email'),
      phone: getValue('text_mm0665eq'),
      title: getValue('text_mm06gedg'),
      company: getValue('text_mm06aj3n'),
      status: 'Active',
      source: getValue('text_mm06h4z9') || 'Marketing Hub',
      notes: '',
      createdAt: '',
      updatedAt: '',
      owner: getValue('text_mm06wy2h') || 'Unassigned',
    };
  } catch (error) {
    logger.simpleError('[Monday] Error finding contact by email:', error);
    return null;
  }
}

/**
 * Create a new contact in the Contacts board using fuzzy column matching
 */
export async function createContact(
  core: MondayCore,
  contact: ContactData,
  options?: { listName?: string }
): Promise<string | null> {
  const config = core.getConfig();

  logger.log(`[Monday] createContact called with:`, {
    email: contact.email,
    listName: options?.listName,
    contactsBoardId: config.contactsBoardId,
  });

  if (!config.contactsBoardId) {
    logger.simpleWarn('[Monday] Contacts board not configured');
    return null;
  }

  try {
    // Build field data for fuzzy matching
    const fieldData: Record<string, unknown> = {};

    // Map contact fields to Monday column names (using common variations)
    if (contact.firstName) fieldData['First Name'] = contact.firstName;
    if (contact.lastName) fieldData['Last Name'] = contact.lastName;
    if (contact.email) fieldData['Email'] = contact.email;
    if (contact.title) fieldData['Title'] = contact.title;
    if (contact.company) fieldData['Company'] = contact.company;
    if (contact.phone) fieldData['Phone'] = contact.phone;
    if (contact.seniority) fieldData['Seniority'] = contact.seniority;
    if (contact.departments) fieldData['Departments'] = contact.departments;
    if (contact.owner) fieldData['Contact Owner'] = contact.owner;
    if (contact.website) fieldData['Website'] = contact.website;
    if (contact.linkedinUrl) fieldData['LinkedIn'] = contact.linkedinUrl;
    if (contact.city) fieldData['City'] = contact.city;
    if (contact.state) fieldData['State'] = contact.state;
    if (contact.country) fieldData['Country'] = contact.country;
    if (contact.industry) fieldData['Industry'] = contact.industry;
    if (contact.employeeCount) fieldData['# Employees'] = contact.employeeCount;
    if (contact.technologies) fieldData['Technologies'] = contact.technologies;

    // Discover column mappings using fuzzy matching (for contact data fields only)
    // Force refresh to ensure we have the latest column types
    const fieldNames = Object.keys(fieldData);
    const mappings = await core.discoverColumnMappings(config.contactsBoardId, fieldNames, true);

    // Build column values using discovered mappings
    const columnValues: Record<string, unknown> = {};

    // Column types that can't accept arbitrary text values
    const unsupportedColumnTypes = ['dropdown', 'status', 'color'];

    for (const [fieldName, value] of Object.entries(fieldData)) {
      const mapping = mappings.get(fieldName);
      if (!mapping) {
        logger.log(`[Monday] No column mapping found for "${fieldName}", skipping`);
        continue;
      }

      // Skip dropdown/status columns - they require specific predefined values
      const columnType = (mapping as { columnType?: string }).columnType;
      if (columnType && unsupportedColumnTypes.includes(columnType)) {
        logger.log(`[Monday] Skipping "${fieldName}" - column type "${columnType}" doesn't support arbitrary values`);
        continue;
      }

      // Transform value based on field type
      const transformedValue = core.transformValueForMonday(fieldName, value, mapping.title);
      // Skip null values (e.g., phone fields are skipped due to API issues)
      if (transformedValue !== null) {
        columnValues[mapping.id] = transformedValue;
      }
    }

    // Explicitly set Enriched and Enrichment Date columns (status fields, not from CSV)
    // Fetch board to find the columns by name
    const board = await core.getBoard(config.contactsBoardId);
    if (board?.columns) {
      // Find Enriched column (checkbox type)
      const enrichedColumn = board.columns.find(
        (col) => col.title.toLowerCase().startsWith('enriched') || col.id === 'enriched' || (col.id.startsWith('boolean_') && col.title.toLowerCase().includes('enriched'))
      );
      if (enrichedColumn) {
        columnValues[enrichedColumn.id] = { checked: 'true' };
        logger.log(`[Monday] Setting Enriched column: ${enrichedColumn.id}`);
      } else {
        logger.log('[Monday] Enriched column not found on contacts board');
      }

      // Find Enrichment Date column (date type)
      const enrichmentDateColumn = board.columns.find(
        (col) => col.title.toLowerCase() === 'enrichment date' || col.id === 'enrichment_date'
      );
      if (enrichmentDateColumn) {
        columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
        logger.log(`[Monday] Setting Enrichment Date column: ${enrichmentDateColumn.id}`);
      } else {
        logger.log('[Monday] Enrichment Date column not found on contacts board');
      }
    }

    // Link to Account board if accountId provided (special handling for board relations)
    if (contact.accountId) {
      // Find the account connection column
      const accountMapping = await core.discoverColumnMappings(
        config.contactsBoardId,
        ['Account', 'Company', 'Accounts']
      );
      const accountColumn = accountMapping.get('Account') || accountMapping.get('Company') || accountMapping.get('Accounts');
      if (accountColumn) {
        columnValues[accountColumn.id] = {
          linkedPulseIds: [{ linkedPulseId: parseInt(contact.accountId) }],
        };
      } else {
        // Fallback to common connect_boards column
        columnValues['connect_boards'] = {
          linkedPulseIds: [{ linkedPulseId: parseInt(contact.accountId) }],
        };
      }
    }

    // Build item name from first + last name or just name
    const itemName = contact.name ||
      [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
      contact.email;

    // Find or create group if listName is provided
    let groupId: string | undefined;
    if (options?.listName) {
      logger.log(`[Monday] Finding or creating group for contact list: ${options.listName}`);
      groupId = await core.findOrCreateGroup(config.contactsBoardId, options.listName);
    }

    const result = await mondayClient.post<{ id: string; name: string }>(
      '/hub/items',
      {
        boardId: config.contactsBoardId,
        groupId: groupId ?? undefined,
        itemName,
        columnValues,
      },
    );
    logger.log(`[Monday] Created contact: ${itemName} (ID: ${result.id})`);
    return result.id;
  } catch (error) {
    logger.simpleError('[Monday] Error creating contact:', error);
    return null;
  }
}

/**
 * Update an existing contact in the Contacts board using fuzzy column matching
 */
export async function updateContact(
  core: MondayCore,
  contactId: string,
  updates: ContactUpdates
): Promise<boolean> {
  const config = core.getConfig();

  if (!config.contactsBoardId) {
    logger.simpleWarn('[Monday] Contacts board not configured');
    return false;
  }

  try {
    // Build field data for fuzzy matching (only include non-undefined values)
    const fieldData: Record<string, unknown> = {};

    if (updates.firstName !== undefined) fieldData['First Name'] = updates.firstName;
    if (updates.lastName !== undefined) fieldData['Last Name'] = updates.lastName;
    if (updates.email !== undefined) fieldData['Email'] = updates.email;
    if (updates.title !== undefined) fieldData['Title'] = updates.title;
    if (updates.company !== undefined) fieldData['Company'] = updates.company;
    if (updates.phone !== undefined) fieldData['Phone'] = updates.phone;
    if (updates.seniority !== undefined) fieldData['Seniority'] = updates.seniority;
    if (updates.departments !== undefined) fieldData['Departments'] = updates.departments;
    if (updates.owner !== undefined) fieldData['Contact Owner'] = updates.owner;
    if (updates.website !== undefined) fieldData['Website'] = updates.website;
    if (updates.linkedinUrl !== undefined) fieldData['LinkedIn'] = updates.linkedinUrl;
    if (updates.city !== undefined) fieldData['City'] = updates.city;
    if (updates.state !== undefined) fieldData['State'] = updates.state;
    if (updates.country !== undefined) fieldData['Country'] = updates.country;
    if (updates.industry !== undefined) fieldData['Industry'] = updates.industry;
    if (updates.employeeCount !== undefined) fieldData['# Employees'] = updates.employeeCount;
    if (updates.technologies !== undefined) fieldData['Technologies'] = updates.technologies;

    // Discover column mappings using fuzzy matching (for contact data fields)
    const fieldNames = Object.keys(fieldData);
    const mappings = await core.discoverColumnMappings(config.contactsBoardId, fieldNames);

    // Build column values using discovered mappings
    const columnValues: Record<string, unknown> = {};

    // Column types that can't accept arbitrary text values
    const unsupportedColumnTypes = ['dropdown', 'status', 'color'];

    for (const [fieldName, value] of Object.entries(fieldData)) {
      const mapping = mappings.get(fieldName);
      if (!mapping) {
        logger.log(`[Monday] No column mapping found for "${fieldName}", skipping`);
        continue;
      }

      // Skip dropdown/status columns - they require specific predefined values
      const columnType = (mapping as { columnType?: string }).columnType;
      if (columnType && unsupportedColumnTypes.includes(columnType)) {
        logger.log(`[Monday] Skipping "${fieldName}" - column type "${columnType}" doesn't support arbitrary values`);
        continue;
      }

      // Transform value based on field type
      const transformedValue = core.transformValueForMonday(fieldName, value, mapping.title);
      // Skip null values (e.g., phone fields are skipped due to API issues)
      if (transformedValue !== null) {
        columnValues[mapping.id] = transformedValue;
      }
    }

    // Explicitly set Enriched and Enrichment Date columns (status fields)
    const board = await core.getBoard(config.contactsBoardId);
    if (board?.columns) {
      const enrichedColumn = board.columns.find(
        (col) => col.title.toLowerCase().startsWith('enriched') || col.id === 'enriched' || (col.id.startsWith('boolean_') && col.title.toLowerCase().includes('enriched'))
      );
      if (enrichedColumn) {
        columnValues[enrichedColumn.id] = { checked: 'true' };
      }

      const enrichmentDateColumn = board.columns.find(
        (col) => col.title.toLowerCase() === 'enrichment date' || col.id === 'enrichment_date'
      );
      if (enrichmentDateColumn) {
        columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
      }
    }

    if (Object.keys(columnValues).length === 0) {
      return true; // Nothing to update after mapping
    }

    await mondayClient.patch<{ id: string }>(
      `/hub/items/${contactId}/columns`,
      {
        boardId: config.contactsBoardId,
        itemId: contactId,
        columnValues,
      },
    );

    logger.log(`[Monday] Updated contact ID: ${contactId}`);
    return true;
  } catch (error) {
    logger.simpleError('[Monday] Error updating contact:', error);
    return false;
  }
}

/**
 * Sync contacts to Monday - upsert logic (update if exists, create if not)
 * Also creates account/company if needed
 */
export async function syncContactsToMonday(
  core: MondayCore,
  contacts: ContactData[],
  options?: { listName?: string }
): Promise<SyncResult> {
  const results: SyncResult = {
    created: 0,
    updated: 0,
    failed: 0,
    errors: [],
  };

  // Cache of accounts we've already created/found this session
  const accountCache = new Map<string, { id: string; name: string }>();

  for (const contact of contacts) {
    try {
      // Find or create the account/company first
      let accountId: string | undefined;
      if (contact.company) {
        const cachedAccount = accountCache.get(contact.company.toLowerCase());
        if (cachedAccount) {
          accountId = cachedAccount.id;
        } else {
          const account = await findOrCreateAccount(core, contact.company, {
            website: contact.website,
            industry: contact.industry,
          });
          if (account) {
            accountId = account.id;
            accountCache.set(contact.company.toLowerCase(), account);
          }
        }
      }

      // Check if contact already exists by email
      const existing = await findContactByEmail(core, contact.email);

      if (existing) {
        // Update existing contact
        const success = await updateContact(core, existing.id, {
          firstName: contact.firstName,
          lastName: contact.lastName,
          phone: contact.phone,
          title: contact.title,
          company: contact.company,
          seniority: contact.seniority,
          departments: contact.departments,
          owner: contact.owner,
          website: contact.website,
          linkedinUrl: contact.linkedinUrl,
          city: contact.city,
          state: contact.state,
          country: contact.country,
          industry: contact.industry,
          employeeCount: contact.employeeCount,
          technologies: contact.technologies,
        });

        if (success) {
          results.updated++;
        } else {
          results.failed++;
          results.errors.push(`Failed to update contact: ${contact.email}`);
        }
      } else {
        // Create new contact (with optional group from list name)
        const newId = await createContact(core, {
          ...contact,
          accountId,
        }, { listName: options?.listName });

        if (newId) {
          results.created++;
        } else {
          results.failed++;
          results.errors.push(`Failed to create contact: ${contact.email}`);
        }
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      results.failed++;
      results.errors.push(`Error processing ${contact.email}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  logger.log(`[Monday] Contact sync complete: ${results.created} created, ${results.updated} updated, ${results.failed} failed`);
  return results;
}

// ============================================================================
// Account Functions
// ============================================================================

/**
 * Find an account/company by name in the Accounts board
 */
export async function findAccountByName(
  core: MondayCore,
  companyName: string
): Promise<{ id: string; name: string } | null> {
  const config = core.getConfig();
  const accountsBoardId = config.accountsBoardId;

  if (!accountsBoardId || !companyName) {
    return null;
  }

  try {
    const allItems = await mondayClient.get<HubBoardItem[]>('/hub/accounts');
    if (!Array.isArray(allItems)) return null;
    const exact = allItems.find(i => i.name.toLowerCase() === companyName.toLowerCase());
    const close = allItems.find(i => i.name.toLowerCase().includes(companyName.toLowerCase()));
    const match = exact || close || null;
    return match ? { id: match.id, name: match.name } : null;
  } catch (error) {
    logger.simpleWarn('[Monday] Error finding account:', error);
    return null;
  }
}

/**
 * Create a new account/company in the Accounts board
 */
export async function createAccount(
  core: MondayCore,
  companyName: string,
  data?: AccountData
): Promise<{ id: string; name: string } | null> {
  const config = core.getConfig();
  const accountsBoardId = config.accountsBoardId;

  if (!accountsBoardId || !companyName) {
    logger.simpleWarn('[Monday] Accounts board not configured');
    return null;
  }

  try {
    const columnValues: Record<string, unknown> = {};

    if (data?.website) {
      columnValues['link'] = { url: data.website, text: data.website };
    }
    if (data?.industry) {
      columnValues['text'] = data.industry;
    }

    // Find and set Enriched and Enrichment Date columns
    const board = await core.getBoard(accountsBoardId);
    if (board?.columns) {
      const enrichedColumn = board.columns.find(
        (col) => col.title.toLowerCase().startsWith('enriched') || col.id === 'enriched' || (col.id.startsWith('boolean_') && col.title.toLowerCase().includes('enriched'))
      );
      if (enrichedColumn) {
        columnValues[enrichedColumn.id] = { checked: 'true' };
        logger.log(`[Monday] Setting Account Enriched column: ${enrichedColumn.id}`);
      }

      const enrichmentDateColumn = board.columns.find(
        (col) => col.title.toLowerCase() === 'enrichment date' || col.id === 'enrichment_date'
      );
      if (enrichmentDateColumn) {
        columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
        logger.log(`[Monday] Setting Account Enrichment Date column: ${enrichmentDateColumn.id}`);
      }
    }

    const result = await mondayClient.post<{ id: string; name: string }>(
      '/hub/items',
      {
        boardId: accountsBoardId,
        itemName: companyName,
        columnValues,
      },
    );
    logger.log(`[Monday] Created account: ${companyName} (ID: ${result.id})`);
    return result;
  } catch (error) {
    logger.simpleError('[Monday] Error creating account:', error);
    return null;
  }
}

/**
 * Find or create an account/company
 */
export async function findOrCreateAccount(
  core: MondayCore,
  companyName: string,
  data?: AccountData
): Promise<{ id: string; name: string } | null> {
  // First try to find existing account
  const existing = await findAccountByName(core, companyName);
  if (existing) {
    logger.log(`[Monday] Found existing account: ${companyName} (ID: ${existing.id})`);
    return existing;
  }

  // Create new account
  return createAccount(core, companyName, data);
}

/**
 * Sync companies to the Accounts board - upsert logic (update if exists, create if not)
 * Creates a new group with the list name and adds accounts to it
 */
export async function syncCompaniesToMonday(
  core: MondayCore,
  companies: CompanyData[],
  options?: { listName?: string }
): Promise<SyncResult> {
  const config = core.getConfig();
  const accountsBoardId = config.accountsBoardId;

  logger.log(`[Monday] syncCompaniesToMonday called with:`, {
    companyCount: companies.length,
    listName: options?.listName,
    accountsBoardId,
  });

  if (!accountsBoardId) {
    logger.simpleWarn('[Monday] Accounts board not configured');
    return { created: 0, updated: 0, failed: companies.length, errors: ['Accounts board not configured'] };
  }

  const results: SyncResult = {
    created: 0,
    updated: 0,
    failed: 0,
    errors: [],
  };

  // Find or create group for this list
  let groupId: string | undefined;
  if (options?.listName) {
    logger.log(`[Monday] Finding or creating group for company list: ${options.listName}`);
    groupId = await core.findOrCreateGroup(accountsBoardId, options.listName);
  }

  // Get board columns for finding Enriched and Enrichment Date
  const board = await core.getBoard(accountsBoardId);
  const enrichedColumn = board?.columns?.find(
    (col) => col.title.toLowerCase().startsWith('enriched') || col.id === 'enriched' || (col.id.startsWith('boolean_') && col.title.toLowerCase().includes('enriched'))
  );
  const enrichmentDateColumn = board?.columns?.find(
    (col) => col.title.toLowerCase() === 'enrichment date' || col.id === 'enrichment_date'
  );

  for (const company of companies) {
    try {
      if (!company.company) {
        results.failed++;
        results.errors.push('Company name is required');
        continue;
      }

      // Check if account already exists by name
      const existing = await findAccountByName(core, company.company);

      // Build column values for enrichment fields
      const enrichmentValues: Record<string, unknown> = {};
      if (enrichedColumn) {
        enrichmentValues[enrichedColumn.id] = { checked: 'true' };
      }
      if (enrichmentDateColumn) {
        enrichmentValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
      }

      if (existing) {
        // Update existing account with enrichment data
        logger.log(`[Monday] Updating existing account: ${company.company} (ID: ${existing.id})`);

        try {
          // Update the account with enrichment fields
          if (Object.keys(enrichmentValues).length > 0) {
            await mondayClient.patch<{ id: string }>(
              `/hub/items/${existing.id}/columns`,
              {
                boardId: accountsBoardId,
                itemId: existing.id,
                columnValues: enrichmentValues,
              },
            );
          }
          results.updated++;
        } catch (updateError) {
          logger.simpleError(`[Monday] Error updating account ${company.company}:`, updateError);
          results.failed++;
          results.errors.push(`Failed to update ${company.company}: ${updateError instanceof Error ? updateError.message : 'Unknown error'}`);
        }
      } else {
        // Create new account in the specified group
        logger.log(`[Monday] Creating new account: ${company.company} in group ${groupId || 'default'}`);

        // Build column values
        const columnValues: Record<string, unknown> = {};

        // Add enrichment columns
        if (enrichedColumn) {
          columnValues[enrichedColumn.id] = { checked: 'true' };
        }
        if (enrichmentDateColumn) {
          columnValues[enrichmentDateColumn.id] = { date: new Date().toISOString().split('T')[0] };
        }

        try {
          const result = await mondayClient.post<{ id: string; name: string }>(
            '/hub/items',
            {
              boardId: accountsBoardId,
              groupId: groupId ?? undefined,
              itemName: company.company,
              columnValues,
            },
          );
          logger.log(`[Monday] Created account: ${company.company} (ID: ${result.id})`);
          results.created++;
        } catch (createError) {
          logger.simpleError(`[Monday] Error creating account ${company.company}:`, createError);
          results.failed++;
          results.errors.push(`Failed to create ${company.company}: ${createError instanceof Error ? createError.message : 'Unknown error'}`);
        }
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      results.failed++;
      results.errors.push(`Error processing ${company.company}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  logger.log(`[Monday] Company sync complete: ${results.created} created, ${results.updated} updated, ${results.failed} failed`);
  return results;
}
